import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { Card as SemanticCard, Divider, Grid, Label } from 'semantic-ui-react'
import { AdminPlaidItem } from '../../../reducers/finances/plaidItemReducer'
import { receiveUserPlaidItem } from '../../../reducers/admin/plaidItemReducer'
import {
  DATE_FORMATS_LUXON,
  formatISOFromUTC,
} from '../../../utils/dateHelpers'
import {
  FinancialAccount,
  TransactionSyncState,
} from '../../../reducers/finances/financialAccountsReducer'
import {
  DeclaredUse,
  getDeclaredUse,
} from '../../../utils/financialAccountHelpers'
import {
  Accordion,
  Button,
  Card,
  GridRowColumn,
  Text,
  Modal,
} from '../../../deprecated-components/BaseComponents'
import { useReselector, useToggle } from '../../../utils/sharedHooks'
import {
  AdminBankAccessStatusModal,
  AdminDeleteModal,
  AdminExcludeReportsModal,
  AdminFinancialErrorModal,
  AdminFinancialSuccessModal,
  AdminRemoveModal,
} from './AdminConfirmModals'
import { ResetTransactionSyncDialog } from './ResetTransactionSyncDialog'
import {
  getLinkedManualAccountsByUserId,
  selectAdminAllPlaidItemAccounts,
  selectAdminHiddenAccounts,
} from '../../../selectors/financeSelectors'
import {
  forceNotifyUserReconnection,
  forcePullStatements,
  forcePullTransactions,
  getDateOfLastTransaction,
  updateAdminUserFinancialAccounts,
  updatePlaidStatementsDisabled as updatePlaidStatementsDisabledAction,
} from '../../../actions/admin/adminFinancialAccountActions'
import {
  PlaidConnectionLabel,
  PlaidConnectionStatus,
  derivePlaidConnectionStatus,
} from './plaid-connection-label'
import { SyncErrorClassification } from '../../../actions/plaid/authActions'
import { useAnalyticsTrack } from '@/features/amplitude'
import { Colors } from '../../../styles/theme'
import { fetchAdminRolledOutInstitutionIds } from '../../../actions/userActions'
import {
  selectIsAccountingOpsManager,
  selectRolledOutInstitutionIds,
} from '../../../selectors/user.selectors'
import {
  checkIsInRolledOutInstitutions,
  hasLimitedBankAccess,
  hasPlaidStatements,
  hasPlaidStatementsAccount,
  hasPlaidStatementsDisabledAccount,
} from '@/features/user/settings/helpers'
import { useAppDispatch } from '../../../utils/typeHelpers'
import {
  Icon,
  Tooltip,
  VerticalThreeDotMenu,
  VerticalThreeDotMenuOption,
} from '@/ui'
import { MarkInactiveDialog } from './MarkInactiveDialog'
import { PrimaryUseEffectiveField } from './PrimaryUseEffectiveField'
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons'

const ACCOUNT_CARD_MENU_OPTIONS: VerticalThreeDotMenuOption[] = [
  { key: 'inactivate_account', label: 'Inactivate' },
]

const DECLARED_USE_LABELS: Record<DeclaredUse, string> = {
  [DeclaredUse.business]: 'Business',
  [DeclaredUse.businessWithPersonalCharges]: 'Business — with personal usage',
  [DeclaredUse.personal]: 'Personal',
  [DeclaredUse.personalWithBusinessCharges]: 'Personal — with business usage',
}

const primaryUseSummary = (account: FinancialAccount) => {
  const declaredUse = getDeclaredUse(account)
  return declaredUse ? DECLARED_USE_LABELS[declaredUse] : null
}

interface AdminAccountCardProps {
  account: FinancialAccount
  noRecentPlaidTransactions: boolean
  needsReconnection: boolean
  syncErrorClassification: SyncErrorClassification | null
  userId: number
  isInactive: boolean
  dateOfLastTransaction: string | null
  isOnlyAccount: boolean
}

const AdminAccountCard = ({
  account,
  noRecentPlaidTransactions,
  needsReconnection,
  syncErrorClassification,
  userId,
  isInactive,
  dateOfLastTransaction,
  isOnlyAccount,
}: AdminAccountCardProps) => {
  const dispatch = useAppDispatch()
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false)

  const menuOptions = isOnlyAccount
    ? ACCOUNT_CARD_MENU_OPTIONS.filter((o) => o.key !== 'inactivate_account')
    : ACCOUNT_CARD_MENU_OPTIONS

  const handleMenuSelect = (option: VerticalThreeDotMenuOption) => {
    if (option.key === 'inactivate_account') {
      setInactiveModalOpen(true)
    }
  }

  const markAccountInactive = async () => {
    await updateAdminUserFinancialAccounts(userId, account.id, {
      inactive: true,
    })(dispatch)
  }
  const linkedManualAccounts = useReselector(
    getLinkedManualAccountsByUserId,
    userId
  )
  const rolledOutInstitutionIds = useReselector(selectRolledOutInstitutionIds)
  const isInRolledOutInstitutions = checkIsInRolledOutInstitutions(
    rolledOutInstitutionIds,
    account.plaidInstitutionId ?? ''
  )

  const linkedManualAccount = useMemo(() => {
    if (!linkedManualAccounts) return null
    return linkedManualAccounts.find(
      (manualAccount) =>
        manualAccount?.manualAccount?.financialAccountId === account.id
    )
  }, [linkedManualAccounts, account])

  const showLBA = useMemo(
    () => !hasPlaidStatements(account) && hasLimitedBankAccess(account),
    [account]
  )

  const accountStatus = derivePlaidConnectionStatus({
    isInactive,
    needsReconnection,
    syncErrorClassification,
    noRecentTransactions: noRecentPlaidTransactions,
  })

  const declaredPrimaryUse = primaryUseSummary(account)

  return (
    <Card
      padding={16}
      backgroundColor={
        accountStatus === PlaidConnectionStatus.Disconnected ||
        accountStatus === PlaidConnectionStatus.SyncError
          ? 'accentRed'
          : 'stone'
      }
      variant={
        accountStatus === PlaidConnectionStatus.Inactive
          ? 'disabled'
          : undefined
      }
    >
      <Grid>
        {showLBA && (
          <GridRowColumn>
            <Label color="violet">LBA</Label>
          </GridRowColumn>
        )}
        <GridRowColumn short className="pt-2">
          <div className="flex justify-between items-center">
            <Text
              as="bodySm"
              style={{
                ...(isInRolledOutInstitutions && account.isHidden
                  ? { color: Colors.mediumGray }
                  : {}),
              }}
            >
              <b>
                {account.name}: {account.mask}
              </b>
            </Text>
            {!isInactive &&
              (isOnlyAccount ? (
                <Tooltip>
                  <Tooltip.Trigger asChild>
                    <span className="cursor-pointer text-gray-400">
                      <Icon icon={faQuestionCircle} />
                    </span>
                  </Tooltip.Trigger>
                  <Tooltip.Content
                    header="No actions available"
                    className="max-w-sm"
                    side="right"
                  >
                    <p>
                      This is the only active account under this institution.
                      Inactivating it individually would leave the Plaid item
                      with no active accounts, which is why no actions are
                      available here.
                    </p>
                    <p className="mt-2">
                      Use <strong>Remove Connection</strong> instead. It removes
                      the Plaid item, stops transaction syncing, and preserves
                      existing transactions.
                    </p>
                    <p className="mt-2">
                      <a
                        href="https://www.notion.so/heard/Technical-Support-Plaid-Playbook-10b591be525780f38fb1e84986e1e273"
                        target="_blank"
                        rel="noreferrer"
                        className="underline text-blue-600 hover:text-blue-800"
                      >
                        View documentation
                      </a>
                    </p>
                  </Tooltip.Content>
                </Tooltip>
              ) : (
                <VerticalThreeDotMenu
                  options={menuOptions}
                  onSelect={handleMenuSelect}
                />
              ))}
          </div>
          <Text
            as="bodyXs"
            style={{
              ...(isInRolledOutInstitutions && account.isHidden
                ? { color: Colors.mediumGray }
                : {}),
            }}
          >
            {account.type} - {account.subtype}
          </Text>
          {isInRolledOutInstitutions &&
            account.transactionSyncState === TransactionSyncState.PENDING && (
              <>
                <br />
                <Text
                  as={'bodyXs'}
                  style={{
                    fontStyle: 'italic',
                    color: Colors.orange,
                  }}
                >
                  Pending confirmation by user
                </Text>
              </>
            )}
        </GridRowColumn>
        {linkedManualAccount && (
          <GridRowColumn short>
            <Text as="bodyXs">
              <b>Manual Account Linked</b>
            </Text>
            <Text as="bodyXs">
              <b> Name:</b> {linkedManualAccount?.manualAccount?.name}
            </Text>
          </GridRowColumn>
        )}
        {accountStatus === PlaidConnectionStatus.LowFrequency && (
          <GridRowColumn short>
            <Text as="bodyXs">
              <b>Low-frequency account</b> — no recent transactions
            </Text>
            <Text as="bodyXs">
              <b>Date of last transaction:</b> {dateOfLastTransaction}
            </Text>
          </GridRowColumn>
        )}
        {declaredPrimaryUse && (
          <GridRowColumn short>
            <Text as="bodyXs">
              <b>Declared use:</b> {declaredPrimaryUse}
            </Text>
            {account.primaryUseSetAt && (
              <Text as="bodyXs">
                <b>Declared on:</b>{' '}
                {DateTime.fromISO(account.primaryUseSetAt).toFormat(
                  DATE_FORMATS_LUXON.DISPLAY_SHORT
                )}
              </Text>
            )}
          </GridRowColumn>
        )}
        <GridRowColumn short>
          <PrimaryUseEffectiveField account={account} userId={userId} />
        </GridRowColumn>
      </Grid>
      <MarkInactiveDialog
        open={inactiveModalOpen}
        account={account}
        userId={userId}
        onClose={() => setInactiveModalOpen(false)}
        onMarkInactive={markAccountInactive}
      />
    </Card>
  )
}

export const AdminInstitutionCard = ({
  plaidItem,
  userId,
}: {
  plaidItem: AdminPlaidItem
  userId: number
}) => {
  const [noRecentTransactionsMap, setNoRecentTransactionsMap] = useState<{
    [id: number]: boolean
  }>({})
  const [datesOfLastTransaction, setDatesOfLastTransaction] = useState<{
    [id: number]: string
  }>({})
  const dispatch = useAppDispatch()
  const [deleteModalOpen, toggleDeleteModal] = useToggle()
  const [removeModalOpen, toggleRemoveModal] = useToggle()
  const [bankAccessStatusModalOpen, toggleBankAccessStatusModal] = useToggle()
  const [excludeReportsModalOpen, toggleExcludeReportsModal] = useToggle()
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [notifying, setNotifying] = useState(false)
  const [pulling, setPulling] = useState(false)
  const [pullingStatements, setPullingStatements] = useState(false)
  const [confirmStatementDisable, setConfirmStatementDisable] = useState(false)
  const [resetSyncDialogOpen, setResetSyncDialogOpen] = useState(false)
  const rolledOutInstitutionIds = useReselector(selectRolledOutInstitutionIds)
  const isAccountingOpsManager = useReselector(selectIsAccountingOpsManager)
  const track = useAnalyticsTrack(true)

  const isInRolledOutInstitutions = checkIsInRolledOutInstitutions(
    rolledOutInstitutionIds,
    plaidItem.institutionId
  )

  const visibleAccounts = useReselector(
    selectAdminAllPlaidItemAccounts,
    userId,
    plaidItem.id
  )

  const hiddenAccounts = useReselector(
    selectAdminHiddenAccounts,
    userId,
    plaidItem.id,
    isInRolledOutInstitutions
  )

  const fetchLastTransactionDates = useCallback(async () => {
    const allAccounts = [...(visibleAccounts ?? []), ...(hiddenAccounts ?? [])]
    const accountIds = allAccounts.map((account) => account.id)
    const newDates: { [id: number]: string } = {}
    const newRecents: { [id: number]: boolean } = {}
    for (const accountId of accountIds) {
      const res = await getDateOfLastTransaction(accountId)(dispatch)
      if (res) {
        const dateTime = DateTime.fromISO(res.date, { zone: 'utc' })
        newDates[accountId] = dateTime.toFormat(
          DATE_FORMATS_LUXON.DISPLAY_SHORT
        )
        newRecents[accountId] = DateTime.now().diff(dateTime, 'days').days > 7
      }
    }
    setDatesOfLastTransaction(newDates)
    setNoRecentTransactionsMap(newRecents)
  }, [visibleAccounts, hiddenAccounts, dispatch])

  useEffect(() => {
    fetchLastTransactionDates()
  }, [fetchLastTransactionDates])

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchAdminRolledOutInstitutionIds(userId))
    }
    fetch()
  }, [dispatch, userId])

  const noRecentTransactions = useMemo(() => {
    return Object.values(noRecentTransactionsMap).includes(true)
  }, [noRecentTransactionsMap])

  const notifyUser = async () => {
    setNotifying(true)

    const res = await forceNotifyUserReconnection({
      plaidItemId: plaidItem.id,
    })(dispatch)
    if (!res) {
      setError('notify reconnect')
    } else {
      dispatch(
        receiveUserPlaidItem({
          userId,
          plaidItem: { ...plaidItem, disconnectedOn: new Date().toISOString() },
        })
      )
    }
    track('clicked plaid notify reconnect', {
      fi_name: plaidItem.institutionName,
    })
    setNotifying(false)
  }

  const pullTransactions = async () => {
    setPulling(true)
    const data = {
      plaidItemId: plaidItem.id,
    }
    const res = await forcePullTransactions(data)(dispatch)
    if (!res) {
      setError('force pull')
    }
    track('clicked plaid force pull', {
      fi_name: plaidItem.institutionName,
    })
    setPulling(false)
    if (typeof res !== 'boolean') {
      const numAddedTransactions = res.transactionIds.length
      if (numAddedTransactions > 0) {
        await fetchLastTransactionDates()
        alert(
          `Force pull successful: There are ${numAddedTransactions} new transactions.`
        )
      } else {
        alert('Force pull completed: No recent transactions.')
      }
    }
  }

  const pullStatements = useCallback(async () => {
    setPullingStatements(true)
    const res = await forcePullStatements(plaidItem.id)(dispatch)
    if (!res) {
      setError('force pull statements')
    }
    setSuccessMessage('Force pull successful for statements')
    setPullingStatements(false)
  }, [dispatch, plaidItem.id])

  const displayUpdateBankAccess = useMemo(() => {
    if (!visibleAccounts) return false
    return !visibleAccounts.every(
      (account) => account.statementPermissions === 'plaid_statement'
    )
  }, [visibleAccounts])

  const {
    needsReconnection,
    institutionName,
    itemId,
    syncErrorClassification,
  } = plaidItem

  let className = 'accountCard'

  const isInactive = itemId === 'inactive'

  const connectionStatus = derivePlaidConnectionStatus({
    isInactive,
    needsReconnection,
    syncErrorClassification,
    noRecentTransactions,
  })

  const isDisconnected = connectionStatus === PlaidConnectionStatus.Disconnected
  const isSyncError = connectionStatus === PlaidConnectionStatus.SyncError

  if (!isInactive && (isDisconnected || isSyncError)) {
    className = `${className} needsReconnection`
  }

  const plaidStatementsDisabled = useMemo(
    () => hasPlaidStatementsDisabledAccount(visibleAccounts),
    [visibleAccounts]
  )

  const displayTogglePlaidStatements = useMemo(() => {
    return plaidStatementsDisabled || hasPlaidStatementsAccount(visibleAccounts)
  }, [plaidStatementsDisabled, visibleAccounts])

  const updatePlaidStatementsDisabled = useCallback(async () => {
    setConfirmStatementDisable(false)
    const disable = !plaidStatementsDisabled
    const res = await updatePlaidStatementsDisabledAction(
      plaidItem.id,
      userId,
      disable
    )(dispatch)
    if (!res) {
      setError('toggle plaid statements disabled')
    }
    track('clicked toggle plaid statements disabled', {
      fi_name: plaidItem.institutionName,
      disable,
    })
  }, [
    plaidStatementsDisabled,
    plaidItem,
    userId,
    dispatch,
    track,
    setConfirmStatementDisable,
  ])

  const hasPlaidStatements = useMemo(
    () => hasPlaidStatementsAccount(visibleAccounts),
    [visibleAccounts]
  )

  const hasPlaidStatementsDisabled = useMemo(
    () =>
      !hasPlaidStatements && hasPlaidStatementsDisabledAccount(visibleAccounts),
    [visibleAccounts, hasPlaidStatements]
  )

  return (
    <SemanticCard className={className} fluid>
      <SemanticCard.Content>
        <Grid>
          <GridRowColumn>
            <Label horizontal>plaid</Label>
            <PlaidConnectionLabel
              status={connectionStatus}
              syncErrorClassification={syncErrorClassification}
            />
            {hasPlaidStatements && (
              <Label color="blue" horizontal>
                plaid statements
              </Label>
            )}
            {hasPlaidStatementsDisabled && (
              <Label color="orange" horizontal>
                plaid statements disabled
              </Label>
            )}
          </GridRowColumn>
          <GridRowColumn short>
            <Text as="h2" color={isInactive ? 'darkGray' : undefined}>
              {institutionName}
            </Text>
            <Text as="bodyXs">Plaid Item ID: {plaidItem.itemId}</Text>
          </GridRowColumn>
          {visibleAccounts.map((account) => (
            <GridRowColumn key={account.id} short>
              <AdminAccountCard
                account={account}
                dateOfLastTransaction={datesOfLastTransaction[account.id]}
                noRecentPlaidTransactions={noRecentTransactionsMap[account.id]}
                needsReconnection={needsReconnection}
                syncErrorClassification={syncErrorClassification}
                userId={userId}
                isInactive={isInactive || Boolean(account.inactive)}
                isOnlyAccount={
                  visibleAccounts.filter((a) => !a.inactive).length === 1
                }
              />
            </GridRowColumn>
          ))}
          {isInRolledOutInstitutions && hiddenAccounts.length > 0 && (
            <GridRowColumn>
              <Accordion
                title={`View ${hiddenAccounts.length} hidden`}
                variant={'text'}
                content={hiddenAccounts.map((account) => (
                  <AdminAccountCard
                    key={account.id}
                    account={account}
                    dateOfLastTransaction={datesOfLastTransaction[account.id]}
                    noRecentPlaidTransactions={
                      noRecentTransactionsMap[account.id]
                    }
                    needsReconnection={needsReconnection}
                    syncErrorClassification={syncErrorClassification}
                    userId={userId}
                    isInactive={isInactive}
                    isOnlyAccount={visibleAccounts.length === 1}
                  />
                ))}
              />
            </GridRowColumn>
          )}
          <Divider />
          <GridRowColumn short>
            <Button
              fullWidth
              variant="warning"
              size="medium"
              onClick={toggleDeleteModal}
            >
              Delete Permanently
            </Button>
            {!isInactive && (
              <>
                <Divider />
                {displayUpdateBankAccess && (
                  <Button
                    fullWidth
                    variant="secondary"
                    size="medium"
                    style={{ marginBottom: 8 }}
                    onClick={toggleBankAccessStatusModal}
                  >
                    Update Limited Bank Access
                  </Button>
                )}
                {displayTogglePlaidStatements && (
                  <Button
                    fullWidth
                    variant="secondary"
                    size="medium"
                    style={{ marginBottom: 8 }}
                    onClick={() => setConfirmStatementDisable(true)}
                  >
                    {plaidStatementsDisabled ? 'Enable' : 'Disable'} Plaid
                    Statements
                  </Button>
                )}
                {
                  <Button
                    fullWidth
                    variant="secondary"
                    size="medium"
                    style={{ marginBottom: 8 }}
                    onClick={toggleRemoveModal}
                  >
                    Remove Connection
                  </Button>
                }
                <Button
                  fullWidth
                  variant="secondary"
                  size="medium"
                  style={{ marginBottom: 8 }}
                  loading={pulling}
                  onClick={pullTransactions}
                  disabled={needsReconnection}
                >
                  Force Pull Transactions
                </Button>
                {isAccountingOpsManager && (
                  <Button
                    fullWidth
                    variant="secondary"
                    size="medium"
                    style={{ marginBottom: 8 }}
                    onClick={() => setResetSyncDialogOpen(true)}
                    disabled={needsReconnection}
                  >
                    Reset Transaction Sync
                  </Button>
                )}
                {hasPlaidStatements && (
                  <Button
                    fullWidth
                    variant="secondary"
                    size="medium"
                    style={{ marginBottom: 8 }}
                    loading={pullingStatements}
                    onClick={pullStatements}
                    disabled={needsReconnection}
                  >
                    Force Pull Statements
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="secondary"
                  size="medium"
                  loading={notifying}
                  disabled={!needsReconnection}
                  onClick={notifyUser}
                >
                  Notify Reconnect
                </Button>
                {plaidItem.disconnectedOn && (
                  <Text as="bodyXs" style={{ marginTop: 4 }}>
                    Notified on:{' '}
                    {formatISOFromUTC(
                      plaidItem.disconnectedOn,
                      DATE_FORMATS_LUXON.DISPLAY_SHORT
                    )}
                  </Text>
                )}
              </>
            )}
            {isInactive && (
              <>
                <Divider />
                <Button
                  fullWidth
                  variant="secondary"
                  size="medium"
                  onClick={toggleExcludeReportsModal}
                >
                  Toggle Report Settings
                </Button>
              </>
            )}
          </GridRowColumn>
        </Grid>
      </SemanticCard.Content>
      <AdminFinancialErrorModal
        open={Boolean(error)}
        toggleOpen={() => setError('')}
      />
      <AdminFinancialSuccessModal
        open={Boolean(successMessage)}
        message={successMessage}
        toggleOpen={() => setSuccessMessage('')}
      />
      <AdminDeleteModal
        open={deleteModalOpen}
        toggleOpen={toggleDeleteModal}
        plaidItem={plaidItem}
        userId={userId}
        setError={(errorType: string) => setError(errorType)}
      />
      <AdminRemoveModal
        open={removeModalOpen}
        toggleOpen={toggleRemoveModal}
        plaidItem={plaidItem}
        userId={userId}
        accounts={visibleAccounts}
        setError={(errorType: string) => setError(errorType)}
      />
      <AdminBankAccessStatusModal
        open={bankAccessStatusModalOpen}
        toggleOpen={toggleBankAccessStatusModal}
        accounts={visibleAccounts}
        plaidItem={plaidItem}
      />
      {excludeReportsModalOpen && (
        <AdminExcludeReportsModal
          open={excludeReportsModalOpen}
          toggleOpen={toggleExcludeReportsModal}
          accounts={plaidItem.accounts}
          userId={userId}
        />
      )}
      {resetSyncDialogOpen && (
        <ResetTransactionSyncDialog
          onClose={() => setResetSyncDialogOpen(false)}
          plaidItem={plaidItem}
        />
      )}
      <Modal size="small" open={confirmStatementDisable}>
        <Modal.Header>
          {plaidStatementsDisabled ? 'Re-enable' : 'Disable'} Plaid Statements
        </Modal.Header>
        <Modal.Content>
          Are you sure you want to{' '}
          {plaidStatementsDisabled ? 're-enable' : 'disable'} Plaid statements?
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setConfirmStatementDisable(false)}
            variant="primary"
          >
            Cancel
          </Button>
          <Button onClick={updatePlaidStatementsDisabled} variant="warning">
            {plaidStatementsDisabled ? 'Re-enable' : 'Disable'} Plaid Statements
          </Button>
        </Modal.Actions>
      </Modal>
    </SemanticCard>
  )
}
